import { createAuthFetch } from "../utils/authFetch";
import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import ProductResults from '../components/ProductResults';
import { useFilters } from "../context/GlobalFilterContext";
import FilterBar from "../components/FilterBar";
import { normalizeAccount } from "../utils/normalizeAccount";

import AnalysisChartProducts from "../components/analytics/AnalysisChartProducts";

function Dashboard() {
    const navigate = useNavigate();
    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const asinFromQuery = queryParams.get("asin") || "";

    const [filters, setFilters] = useState(() => {
      const today = new Date();
      const dateFrom = new Date();
      dateFrom.setMonth(today.getMonth() - 18);

      const formatDate = (d) => d.toISOString().slice(0, 10);

      return {
        period: "18_months",
        dateFrom: formatDate(dateFrom),
        dateTo: formatDate(today),
        search: asinFromQuery,
        type: "",
        sort: "",
      };
    });

    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedAsins, setSelectedAsins] = useState(new Set());
    const [showAnalytics, setShowAnalytics] = useState(false);
    const { account, marketplace } = useFilters();

    const [detailsPanel, setDetailsPanel] = useState({
      open: false,
      monthIndex: null,
      monthLabel: null,
    });

    const authFetch = createAuthFetch(navigate);

    const widgetsRef = useRef(null);
    const scrollToWidgets = () => {
      setShowAnalytics(true);

      requestAnimationFrame(() => {
        widgetsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    };

    useEffect(() => {
      if (!asinFromQuery) return;

      setFilters((prev) => ({
        ...prev,
        search: asinFromQuery,
      }));

      setShowAnalytics(true);
    }, [asinFromQuery]);

      // 🔹 initial + filters fetch
    useEffect(() => {
        fetchProducts();
        // eslint-disable-next-line
    }, [filters,account,marketplace]);

    const fetchProducts = async () => {
        setIsLoading(true);
        setSelectedAsins(new Set());

        const normalizedAccount = normalizeAccount(account);

        const body = {
            account:normalizedAccount,
            marketplace,
            search: filters.search || null,
            start: filters.dateFrom,
            end: filters.dateTo,
            type: filters.type || null,
            sort: filters.sort || null,
          };

        try {
          const res = await authFetch(`/amz-api/products/views/dashboard`, {
            method: 'POST',
            body: JSON.stringify(body),
          });
          if (!res) return;

          const data = await res.json();

          setProducts(data.data || []);
        } finally {
          setIsLoading(false);
        }
      };

      useEffect(() => {
        if (!asinFromQuery || !products.length) return;

        const matchedProduct = products.find((p) => p.asin === asinFromQuery);

        if (matchedProduct) {
          setSelectedAsins(new Set([asinFromQuery]));
          setShowAnalytics(true);

          requestAnimationFrame(() => {
            widgetsRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          });
        }
      }, [asinFromQuery, products]);

      // 🔹 toggle one ASIN
      const toggleProduct = useCallback((asin) => {
        setSelectedAsins(prev => {
          const next = new Set(prev);
          next.has(asin) ? next.delete(asin) : next.add(asin);
          return next;
        });
      }, []);

      // 🔹 toggle all visible
      const toggleAll = useCallback(() => {
        setSelectedAsins(prev => {
          if (prev.size === products.length) {
            return new Set();
          }
          return new Set(products.map(p => p.asin));
        });
      }, [products]);

  return (
    <div className="container-fluid main-container h-100 flex-grow-1">
      <div className="row">
        <div className="col">
          <h1>Dashboard</h1>

          <FilterBar
            visibleFilters={["search","type","date","sort"]}
            defaultValues={filters}
            onApply={(f) => {
              setFilters(f);
            }}
          />

          {!showAnalytics && (
            <div className="text-muted">
              Select products and click “View analytics”
            </div>
          )}

          <ProductResults
                  products={products}
                  selectedAsins={selectedAsins}
                  onToggleProduct={toggleProduct}
                  onToggleAll={toggleAll}
                  isLoading={isLoading}
                  onViewAnalytics={scrollToWidgets}
                />

          <div ref={widgetsRef} className="widgets-section mt-4">
                <h4>Analytics</h4>

                {showAnalytics && selectedAsins.size > 0 && (
                    <div className="dashboard-widget mt-3 mb-3">
                      <div className="widget-header">
                        <h5>Products analytics</h5>
                      </div>

                      <div className="widget-body">
                        <AnalysisChartProducts
                          account={account}
                          marketplace={marketplace}
                          start={filters.dateFrom}
                          end={filters.dateTo}
                          asins={[...selectedAsins]}
                          onViewDetails={(payload) => {
                              setDetailsPanel({
                                open: true,
                                monthIndex: payload.index,
                                monthLabel: payload.label,
                              });
                            }}
                        />
                      </div>
                    </div>
                  )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default Dashboard;
